// src/pages/AdminDashboard.js
//
// Admin-only dashboard. Collapsible sections with status badges:
//   - Pending Registrations
//   - Pool Stats
//   - Round Status
//   - Incomplete Picks

import React, { useState } from 'react';

function StatCard({ label, value, color }) {
    return (
        <div className="bg-gray-900 border border-gray-800 rounded-xl px-5 py-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</p>
            <p className={'text-3xl font-black ' + (color || 'text-white')}>{value ?? '—'}</p>
        </div>
    );
}

function DeadlineBadge({ deadline }) {
    if (!deadline) return <span className="text-gray-600 text-xs">No deadline</span>;
    const now  = new Date();
    const due  = new Date(deadline);
    const diff = due - now;
    const mins = Math.floor(diff / 60000);

    if (diff < 0) return <span className="text-gray-500 text-xs">Passed</span>;
    if (mins < 60) return <span className="text-red-400 text-xs font-bold">{mins}m remaining</span>;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return <span className="text-yellow-400 text-xs font-bold">{hrs}h remaining</span>;
    return (
        <span className="text-gray-400 text-xs">
            {due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
        </span>
    );
}

function SectionPanel({ title, badge, defaultOpen, children }) {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden mb-4">
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-800/50 transition-colors">
                <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{title}</span>
                    {badge}
                </div>
                <span className="text-gray-600 text-sm font-bold">{open ? '▲' : '▼'}</span>
            </button>
            {open && (
                <div className="border-t border-gray-800">
                    {children}
                </div>
            )}
        </div>
    );
}

function StatusBadge({ color, label }) {
    const colors = {
        red:    'bg-red-900/50 text-red-400 border-red-800',
        amber:  'bg-yellow-900/50 text-yellow-400 border-yellow-800',
        green:  'bg-green-900/50 text-green-400 border-green-800',
        gray:   'bg-gray-800 text-gray-500 border-gray-700',
    };
    return (
        <span className={'text-xs font-bold px-2 py-0.5 rounded border ' + (colors[color] || colors.gray)}>
            {label}
        </span>
    );
}

function PoolIncompletePanel({ pool, gameCount }) {
    const [open, setOpen] = useState(false);
    const count = pool.incomplete.length;

    return (
        <div className="border-t border-gray-800/50">
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full px-6 py-2 bg-gray-800/20 flex items-center justify-between hover:bg-gray-800/40 transition-colors">
                <div className="flex items-center gap-3">
                    <p className="text-white text-xs font-bold">{pool.pool_name}</p>
                    <span className="text-xs font-bold px-2 py-0.5 rounded border bg-red-900/40 text-red-400 border-red-900">
                        {count} participant{count !== 1 ? 's' : ''} behind
                    </span>
                </div>
                <span className="text-gray-600 text-xs font-bold">{open ? '▲' : '▼'}</span>
            </button>
            {open && (
                <div className="divide-y divide-gray-800/30">
                    {pool.incomplete.map(entry => {
                        const missing = gameCount - entry.pick_count;
                        return (
                            <div key={entry.entry_id} className="px-8 py-2 flex items-center justify-between">
                                <span className="text-white text-sm">{entry.participant_name}</span>
                                <span className="text-red-400 text-xs font-bold">{missing} missing</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function RoundIncompletePanel({ round }) {
    const [open, setOpen] = useState(false);
    const count = round.distinct_behind;

    return (
        <div>
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full px-4 py-2.5 bg-gray-800/40 flex items-center justify-between hover:bg-gray-800/70 transition-colors">
                <div className="flex items-center gap-3">
                    <p className="font-bold text-white text-xs uppercase tracking-wide">{round.round_name}</p>
                    <span className="text-xs font-bold px-2 py-0.5 rounded border bg-red-900/50 text-red-400 border-red-800">
                        {count} participant{count !== 1 ? 's' : ''} behind
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <DeadlineBadge deadline={round.pick_deadline} />
                    <span className="text-gray-600 text-xs font-bold">{open ? '▲' : '▼'}</span>
                </div>
            </button>
            {open && (
                <div>
                    {round.pools.map(p => (
                        <PoolIncompletePanel key={p.pool_id} pool={p} gameCount={round.game_count} />
                    ))}
                </div>
            )}
        </div>
    );
}

function AdminDashboard({ stats, incompletePicks, pendingRegCount, rounds, activeTournament, onNavigateToRegistrations }) {

    // Derive status for each section to set badge + default open state.
    const hasPendingRegs  = pendingRegCount > 0;
    const hasIncomplete   = incompletePicks.length > 0;
    const totalIncomplete = incompletePicks.reduce((sum, r) => sum + r.distinct_behind, 0);

    const allRoundsComplete = rounds.length > 0 &&
        rounds.every(r => parseInt(r.games_final) >= r.game_count && r.game_count > 0);
    const anyRoundActive = rounds.some(r => parseInt(r.games_final) < r.game_count && r.game_count > 0);

    const roundsBadge = rounds.length === 0
        ? <StatusBadge color="gray"  label="No rounds" />
        : allRoundsComplete
            ? <StatusBadge color="green" label="All complete" />
            : anyRoundActive
                ? <StatusBadge color="amber" label="In progress" />
                : <StatusBadge color="gray"  label="Not started" />;

    const incompleteBadge = hasIncomplete
        ? <StatusBadge color="red"   label={`${totalIncomplete} participant${totalIncomplete !== 1 ? 's' : ''} behind`} />
        : rounds.length > 0
            ? <StatusBadge color="green" label="All caught up" />
            : <StatusBadge color="gray"  label="No open rounds" />;

    const regBadge = hasPendingRegs
        ? <StatusBadge color="red"   label={`${pendingRegCount} pending`} />
        : <StatusBadge color="green" label="None pending" />;

    const statsBadge = !stats
        ? <StatusBadge color="gray" label="No pool data" />
        : null;

    return (
        <div>
            {/* Pending Registrations */}
            <SectionPanel
                title="Pending Registrations"
                badge={regBadge}
                defaultOpen={hasPendingRegs}>
                {hasPendingRegs ? (
                    <div className="px-4 py-4 flex items-center justify-between">
                        <p className="text-sm text-white">
                            <span className="font-bold text-red-400">{pendingRegCount}</span>
                            {' '}participant{pendingRegCount !== 1 ? 's' : ''} waiting for approval.
                        </p>
                        <button
                            onClick={onNavigateToRegistrations}
                            className="text-xs font-bold text-orange-400 hover:text-orange-300 border border-orange-800 hover:border-orange-600 rounded-lg px-3 py-1.5 transition-colors">
                            Review →
                        </button>
                    </div>
                ) : (
                    <div className="px-4 py-4 text-sm text-gray-500">No pending registrations.</div>
                )}
            </SectionPanel>

            {/* Pool Stats */}
            <SectionPanel
                title="Pool Stats"
                badge={statsBadge}
                defaultOpen={true}>
                {stats ? (
                    <div className="px-4 py-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                            <StatCard label="Entries"       value={stats.total_entries}                              color="text-blue-400" />
                            <StatCard label="Games final"   value={`${stats.games_final} / ${stats.total_games}`}   color="text-green-400" />
                            <StatCard label="Correct picks" value={stats.correct_picks}                              color="text-yellow-400" />
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            <StatCard label="Total picks"   value={stats.total_picks} />
                            <StatCard label="Pending picks" value={stats.pending_picks} color="text-gray-400" />
                            <StatCard label="Pool type"     value={stats.pool_type} />
                        </div>
                    </div>
                ) : (
                    <div className="px-4 py-4 text-sm text-gray-500">No pool data available for this tournament.</div>
                )}
            </SectionPanel>

            {/* Round Status */}
            <SectionPanel
                title="Round Status"
                badge={roundsBadge}
                defaultOpen={anyRoundActive && !allRoundsComplete}>
                {rounds.length === 0 ? (
                    <div className="px-4 py-4 text-sm text-gray-500">No rounds configured for this tournament.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-800">
                                <th className="text-left px-4 py-2 text-xs text-gray-500 font-bold uppercase tracking-wide">Round</th>
                                <th className="text-center px-4 py-2 text-xs text-gray-500 font-bold uppercase tracking-wide">Games</th>
                                <th className="text-right px-4 py-2 text-xs text-gray-500 font-bold uppercase tracking-wide">Deadline</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rounds
                                .slice()
                                .sort((a, b) => a.round_number - b.round_number)
                                .map((r, i) => {
                                    const gf = parseInt(r.games_final) || 0;
                                    const allFinal = gf >= r.game_count && r.game_count > 0;
                                    return (
                                        <tr key={r.id} className={i < rounds.length - 1 ? 'border-b border-gray-800/50' : ''}>
                                            <td className="px-4 py-2.5 text-white font-medium">
                                                {r.round_name}
                                                {allFinal && (
                                                    <span className="ml-2 text-xs text-green-500 font-bold">✓</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2.5 text-center text-gray-400">
                                                {gf} / {r.game_count}
                                            </td>
                                            <td className="px-4 py-2.5 text-right">
                                                <DeadlineBadge deadline={r.pick_deadline} />
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                )}
            </SectionPanel>

            {/* Incomplete Picks */}
            <SectionPanel
                title="Incomplete Picks"
                badge={incompleteBadge}
                defaultOpen={hasIncomplete}>
                {!hasIncomplete ? (
                    <div className="px-4 py-4 text-sm text-gray-500">
                        {rounds.length === 0
                            ? 'No open rounds.'
                            : 'All participants are up to date on open rounds.'}
                    </div>
                ) : (
                    <div className="divide-y divide-gray-800">
                        {incompletePicks.map(round => (
                            <RoundIncompletePanel key={round.round_id} round={round} />
                        ))}
                    </div>
                )}
            </SectionPanel>
        </div>
    );
}

export default AdminDashboard;